import React, { type JSX, useEffect, useRef } from 'react';

import { useQuery } from '@apollo/client/react';
import { Navigate } from 'react-router';

import { getApolloGraphQLErrors } from '../apolloError';
import { FullPageHomeLoader } from '../components/FullPageHomeLoader';
import { hasAnyUnauthenticatedError, Layout } from '../components/Layout';
import { UpdatePartnerIdentifier } from '../config/PartnerContext';
import { GetOrganizationDocument } from '../graphql/queries/__generated__/GetOrganization';
import { GetSessionDataDocument } from '../graphql/queries/__generated__/GetSessionData';
import type { GetSessionData } from '../graphql/queries/__generated__/GetSessionData';
import { WEB_API_URL } from '../helpers/globals';
import { ERROR_PAGE_ROUTE } from './routes';
import UnauthorizedAccessErrorPage from './UnauthorizedAccessErrorPage';

type GetSessionData_session_human = NonNullable<NonNullable<GetSessionData['session']>['human']>;

// React.lazy()-wrapped components do not expose static properties from the
// underlying default export, so `PrivateRoute` takes the auth predicate and
// dashboard name as explicit props rather than reading them off the component.
type PrivateRouteProps = {
  isAuthorized?: (sessionHuman: GetSessionData_session_human) => boolean;
  dashboardName?: string;
  component?: React.ComponentType;
  components?: Array<{
    path: string;
    isAuthorized: (sessionHuman: GetSessionData_session_human) => boolean;
  }>;
  renderLogo?: () => JSX.Element | null;
};

const renderLoader = () => <FullPageHomeLoader />;

const PrivateRoute: React.FC<PrivateRouteProps> = (props) => {
  const { renderLogo, component, components } = props;

  useEffect(() => {
    document.body.style.backgroundColor = '#f4f8fb';
    return () => {
      document.body.style.removeProperty('background-color');
    };
  }, []);

  const loadingScreen = <Layout renderLogo={renderLogo} renderContent={renderLoader} />;

  const {
    data: sessionData,
    error: sessionError,
    loading: sessionLoading,
  } = useQuery(GetSessionDataDocument);
  const { data: orgData, error: orgError } = useQuery(GetOrganizationDocument);

  if (sessionLoading) {
    return loadingScreen;
  }
  if (sessionError) {
    if (hasAnyUnauthenticatedError(getApolloGraphQLErrors(sessionError))) {
      return <RedirectToLogin />;
    }
    return <Navigate to={ERROR_PAGE_ROUTE} replace />;
  }
  if (
    !sessionData ||
    !sessionData.session ||
    !sessionData.session.logged_in ||
    !sessionData.session.human
  ) {
    return <RedirectToLogin />;
  }
  const sessionHuman = sessionData.session.human as GetSessionData_session_human;
  let unauthorized = true;
  let componentToRender: JSX.Element | null = null;

  if (component) {
    unauthorized = props.isAuthorized ? !props.isAuthorized(sessionHuman) : false;
    const Component = component;
    componentToRender = <Component />;
  } else if (components) {
    const matchedComponent = components.find((c) => c.isAuthorized(sessionHuman));
    unauthorized = !matchedComponent;
    if (matchedComponent) {
      return <Navigate to={matchedComponent.path} replace />;
    }
  }
  if (unauthorized) {
    return (
      <UnauthorizedAccessErrorPage
        dashboardName={props.dashboardName}
        userEmail={sessionHuman.email ?? ''}
        loadingScreen={loadingScreen}
      />
    );
  }
  if (orgError) {
    return <Navigate to={ERROR_PAGE_ROUTE} replace />;
  }
  if (orgData && orgData.organization?.identifier) {
    return (
      <>
        {/* A component rather than an inline setter call: it writes to context from an
            effect, which keeps this render free of side effects. */}
        <UpdatePartnerIdentifier identifier={orgData.organization.identifier} />
        {componentToRender}
      </>
    );
  }
  return null;
};

export const RedirectToLogin = () => {
  const hasRedirected = useRef(false);
  useEffect(() => {
    if (!hasRedirected.current) {
      hasRedirected.current = true;
      const loginAndRedirectURL = `${WEB_API_URL}/login?redirectTo=${window.location.href}`;
      window.location.assign(loginAndRedirectURL);
    }
  }, []);
  return null;
};

export default PrivateRoute;
